import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import EpisodeMentionReviewDialog, {
  areAllReviewMentionsReady,
  type MentionReviewSession,
  type MentionSaveSource,
} from "../components/EpisodeMentionReviewDialog";
import BrandStringListEditor from "../components/BrandStringListEditor";
import { type VisualCue } from "../utils/visualCueLabels";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  CircularProgress,
  Avatar,
  Button,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  ListItemButton,
  Snackbar,
  Tabs,
  Tab,
  Stack,
  Divider,
  Autocomplete,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  YouTube as YouTubeIcon,
  MusicNote as MusicNoteIcon,
  Podcasts as PodcastIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  AccountTree as AccountTreeIcon,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import apiService from "../services/api";
import { getBrandLogoProps, hasBrandLogo } from "../utils/brandLogoUtils";
import ReviewEpisodeSection from "../components/ReviewEpisodeSection";
import ReviewProgramsPanel from "../components/ReviewProgramsPanel";
import ReviewBrandFilterBar from "../components/ReviewBrandFilterBar";
import ReviewDayRangePicker from "../components/ReviewDayRangePicker";
import { ContentItem, BrandMention } from "../types";
import {
  initialSelectedWeekForReviewMode,
  useFilterStore,
  type ReviewStatusFilter,
} from "../stores/filterStore";
import { buildMonthOptions, buildWeekOptions } from "../utils/reviewDateFilterOptions";
import { useDebounce } from "../utils/useDebounce";
import {
  BrandTab,
  contentIdForApi,
  usePageBrandMentions,
  BRAND_MENTIONS_BATCH_QUERY_KEY,
  type BatchMentionsQueryData,
} from "../hooks/usePageBrandMentions";

/** Muted surface fill that works in light and dark mode. */
const subtleMutedSurfaceBg = (theme: Theme) =>
  theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.06)
    : theme.palette.grey[50];

/** Subtle row background for brand list cards (light + dark mode). */
const subtleBrandListRowSx = (theme: Theme) => ({
  mb: 1,
  p: 0,
  borderRadius: 1,
  bgcolor: subtleMutedSurfaceBg(theme),
});

const reviewFilterSelectMenuProps = {
  disableScrollLock: true,
  slotProps: {
    paper: {
      sx: { maxHeight: 320 },
    },
  },
} as const;

// Helper function to normalize text for search (removes accents)
const normalizeText = (text: string): string => {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, ""); // Remove accent marks
};

// Enhanced brand search function
const searchBrands = (brands: any[], query: string): any[] => {
  if (!query.trim()) {
    return brands;
  }

  const normalizedQuery = normalizeText(query);
  return brands.filter((brand: any) => {
    // Check brand name (with and without accent normalization)
    const brandNameMatch =
      brand.name.toLowerCase().includes(query.toLowerCase()) ||
      normalizeText(brand.name).includes(normalizedQuery);

    // Check aliases (with and without accent normalization)
    const aliasMatch = brand.aliases?.some(
      (alias: string) =>
        alias.toLowerCase().includes(query.toLowerCase()) ||
        normalizeText(alias).includes(normalizedQuery),
    );

    return brandNameMatch || aliasMatch;
  });
};

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  },
);

function sortAllEpisodeMentions(mentions: BrandMention[]): BrandMention[] {
  return [...mentions].sort((a, b) => a.startTime - b.startTime);
}

function sortEpisodeMentions(mentions: BrandMention[]): BrandMention[] {
  return sortAllEpisodeMentions(mentions).filter(
    (m) => m.feedback?.status !== "rejected",
  );
}

/**
 * Applies an optimistic mention edit to the page's batch cache entry.
 * Written through setQueriesData because the entry is keyed by the whole page's ids.
 */
function syncContentMentionsInCache(
  queryClient: QueryClient,
  contentId: string,
  updater: (mentions: BrandMention[]) => BrandMention[],
): BrandMention[] | undefined {
  const key = contentIdForApi(contentId);
  let updated: BrandMention[] | undefined;
  queryClient.setQueriesData<BatchMentionsQueryData>(
    { queryKey: BRAND_MENTIONS_BATCH_QUERY_KEY },
    (old) => {
      const current = old?.mentionsByContentId?.[key];
      if (!current) return old;
      updated = updater(current);
      return {
        ...old,
        mentionsByContentId: { ...old!.mentionsByContentId, [key]: updated },
      };
    },
  );
  return updated;
}

function mapCreatedMentionToBrandMention(
  raw: {
    _id?: string | { toString(): string };
    brandName?: string;
    mentionType?: string;
    commercialConfidence?: number;
    confidence?: number;
    segmentStart?: number;
    segmentEnd?: number;
    context?: string;
    contentId?: string | { toString(): string };
    brandId?: string | { toString(): string };
  },
  source: BrandMention,
): BrandMention {
  return {
    _id: raw._id?.toString?.() ?? String(raw._id),
    brandName: raw.brandName ?? source.brandName,
    classification: raw.mentionType ?? source.classification,
    commercialConfidence: raw.commercialConfidence ?? source.commercialConfidence,
    confidence: raw.confidence ?? source.confidence,
    startTime: raw.segmentStart ?? source.startTime,
    endTime: raw.segmentEnd ?? source.endTime,
    context: raw.context ?? "",
    contentId: raw.contentId?.toString?.() ?? source.contentId,
    brandId: raw.brandId?.toString?.() ?? source.brandId,
    logoUrl: source.logoUrl,
    s3Key: source.s3Key,
    aliases: source.aliases,
    placementType: source.placementType,
    visualCue: source.visualCue,
    evidenceType: source.evidenceType,
    feedback: (raw as any).feedback ?? null,
    review: (raw as any).review ?? null,
  };
}

function mapApiMentionToBrandMention(
  raw: Record<string, unknown>,
  fallback?: BrandMention,
): BrandMention {
  const id =
    raw._id != null && typeof raw._id === "object" && "toString" in raw._id
      ? String((raw._id as { toString(): string }).toString())
      : String(raw._id ?? fallback?._id ?? "");
  const contentId =
    raw.contentId != null && typeof raw.contentId === "object" && "toString" in raw.contentId
      ? String((raw.contentId as { toString(): string }).toString())
      : String(raw.contentId ?? fallback?.contentId ?? "");

  const feedbackRaw = raw.feedback as BrandMention["feedback"] | undefined;
  const feedback =
    feedbackRaw?.status === "confirmed" ||
    feedbackRaw?.status === "rejected" ||
    feedbackRaw?.status === "approved"
      ? feedbackRaw
      : null;

  return {
    _id: id,
    brandName: String(raw.brandName ?? fallback?.brandName ?? ""),
    classification: String(
      raw.mentionType ?? raw.classification ?? fallback?.classification ?? "paid",
    ),
    commercialConfidence: Number(
      raw.commercialConfidence ?? fallback?.commercialConfidence ?? 0,
    ),
    confidence: Number(raw.confidence ?? fallback?.confidence ?? 0),
    startTime: Number(raw.segmentStart ?? raw.startTime ?? fallback?.startTime ?? 0),
    endTime: Number(raw.segmentEnd ?? raw.endTime ?? fallback?.endTime ?? 0),
    context: String(raw.context ?? fallback?.context ?? ""),
    contentId,
    brandId: raw.brandId
      ? String(raw.brandId)
      : fallback?.brandId,
    logoUrl: (raw.logoUrl as string | undefined) ?? fallback?.logoUrl,
    s3Key: (raw.s3Key as string | undefined) ?? fallback?.s3Key,
    aliases: (raw.aliases as string[] | undefined) ?? fallback?.aliases,
    placementType:
      (raw.placementType as BrandMention["placementType"]) ??
      fallback?.placementType,
    visualCue:
      (raw.visualCue as BrandMention["visualCue"]) ??
      fallback?.visualCue ??
      null,
    evidenceType:
      (raw.evidenceType as BrandMention["evidenceType"]) ??
      fallback?.evidenceType ??
      null,
    feedback,
    review: (raw.review as BrandMention["review"]) ?? fallback?.review ?? null,
    aiClassification:
      (raw.aiClassification as BrandMention["aiClassification"]) ??
      fallback?.aiClassification ??
      null,
    transcriptExcerpt:
      (raw.transcriptExcerpt as BrandMention["transcriptExcerpt"]) ??
      fallback?.transcriptExcerpt,
  };
}

const BrandManagementDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  brandName: string;
  brandId?: string;
  currentAliases: string[];
  mentionCount?: number;
  onAliasesUpdate: (aliases: string[]) => void;
  onAntiAliasesUpdate: (antiAliases: string[]) => void;
  onBrandChange?: (
    oldBrandName: string,
    newBrandId: string,
    newBrandName: string,
  ) => void;
}> = ({
  open,
  onClose,
  brandName,
  brandId,
  currentAliases,
  mentionCount,
  onAliasesUpdate,
  onAntiAliasesUpdate,
  onBrandChange,
}) => {
  const [aliases, setAliases] = useState<string[]>(currentAliases);
  const [antiAliases, setAntiAliases] = useState<string[]>([]);
  const [brandSearchQuery, setBrandSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(brandSearchQuery, 600);
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedBrandId, setSelectedBrandId] = useState("");
  const [activeTab, setActiveTab] = useState<
    "aliases" | "anti-aliases" | "change-brand"
  >("aliases");

  const queryClient = useQueryClient();

  const { data: brandData, isLoading: brandLoading } = useQuery({
    queryKey: ["brand", brandId],
    queryFn: () => apiService.getBrand(brandId!),
    enabled: open && !!brandId,
    staleTime: 0,
  });

  useEffect(() => {
    if (open) {
      setAliases(currentAliases);
      setAntiAliases([]);
      setBrandSearchQuery("");
      setSelectedBrand("");
      setSelectedBrandId("");
      setActiveTab("aliases");
    }
  }, [open, currentAliases]);

  useEffect(() => {
    if (brandData?.brand) {
      setAliases(brandData.brand.aliases || currentAliases);
      setAntiAliases(brandData.brand.antiAliases || []);
    }
  }, [brandData, currentAliases]);

  const { data: brandsData, isFetching: brandsFetching } = useQuery({
    queryKey: ["brands", debouncedSearchQuery || "all"],
    queryFn: () =>
      apiService.getBrands(1, 100, debouncedSearchQuery || undefined),
    enabled: open && onBrandChange !== undefined,
    staleTime: 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  React.useEffect(() => {
    if (
      open &&
      onBrandChange !== undefined &&
      debouncedSearchQuery !== brandSearchQuery
    ) {
      queryClient.cancelQueries({ queryKey: ["brands"] });
    }
  }, [
    debouncedSearchQuery,
    brandSearchQuery,
    open,
    onBrandChange,
    queryClient,
  ]);

  const filteredBrands = React.useMemo(() => {
    const brands = brandsData?.brands || [];
    return searchBrands(brands, brandSearchQuery);
  }, [brandSearchQuery, brandsData?.brands]);

  const handleBrandSelect = (nextBrandId: string, nextBrandName: string) => {
    setSelectedBrandId(nextBrandId);
    setSelectedBrand(nextBrandName);
    setBrandSearchQuery(nextBrandName);
  };

  const handleSave = () => {
    if (activeTab === "aliases") {
      onAliasesUpdate(aliases);
    } else if (activeTab === "anti-aliases") {
      onAntiAliasesUpdate(antiAliases);
    } else if (
      activeTab === "change-brand" &&
      onBrandChange &&
      selectedBrand &&
      selectedBrandId
    ) {
      onBrandChange(brandName, selectedBrandId, selectedBrand);
    }
    onClose();
  };

  const handleCancel = () => {
    setAliases(currentAliases);
    setAntiAliases(brandData?.brand?.antiAliases || []);
    setBrandSearchQuery("");
    setSelectedBrand("");
    setSelectedBrandId("");
    setActiveTab("aliases");
    onClose();
  };

  const canSave =
    activeTab === "aliases" ||
    activeTab === "anti-aliases" ||
    (activeTab === "change-brand" && !!selectedBrand && !!selectedBrandId);

  const saveLabel =
    activeTab === "aliases"
      ? "Save Aliases"
      : activeTab === "anti-aliases"
        ? "Save Anti-aliases"
        : `Change to ${selectedBrand || "Selected Brand"}`;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
      sx={{ "& .MuiDialog-paper": { height: "80vh", maxHeight: "800px" } }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Manage Brand: {brandName}</Typography>
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
        >
          <Tab label="Aliases" value="aliases" />
          <Tab label="Anti-aliases" value="anti-aliases" />
          {onBrandChange && mentionCount ? (
            <Tab
              label={`Change Brand (${mentionCount} mention${mentionCount > 1 ? "s" : ""})`}
              value="change-brand"
            />
          ) : null}
        </Tabs>

        {brandLoading && activeTab !== "change-brand" ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        ) : null}

        {activeTab === "aliases" && !brandLoading ? (
          <>
            {!brandId ? (
              <MuiAlert severity="warning" sx={{ mb: 2 }}>
                This mention is not linked to a brand record yet. Migrate it
                using the Change Brand tab first.
              </MuiAlert>
            ) : null}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Alternative names that should match this brand during detection.
            </Typography>
            <BrandStringListEditor
              items={aliases}
              onChange={setAliases}
              addLabel="Add alias"
              listLabel="Current aliases"
              emptyMessage="No aliases defined for this brand"
            />
          </>
        ) : null}

        {activeTab === "anti-aliases" && !brandLoading ? (
          <>
            {!brandId ? (
              <MuiAlert severity="warning" sx={{ mb: 2 }}>
                This mention is not linked to a brand record yet. Migrate it
                using the Change Brand tab first.
              </MuiAlert>
            ) : null}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Words or phrases that indicate this is not the brand. Detection
              will skip mentions when these appear in context.
            </Typography>
            <BrandStringListEditor
              items={antiAliases}
              onChange={setAntiAliases}
              addLabel="Add anti-alias"
              listLabel="Current anti-aliases"
              emptyMessage="No anti-aliases defined for this brand"
            />
          </>
        ) : null}

        {activeTab === "change-brand" && onBrandChange ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This will change all{" "}
              <strong>
                {mentionCount} mention
                {mentionCount && mentionCount > 1 ? "s" : ""}
              </strong>{" "}
              of "{brandName}" in this podcast to the selected brand.
            </Typography>
            <Typography
              variant="body2"
              color="info.main"
              sx={{ mb: 3, fontStyle: "italic" }}
            >
              Note: "{brandName}" will also be added as an alias to the selected
              brand to maintain the connection.
            </Typography>

            <Box position="relative">
              <TextField
                fullWidth
                label="Search New Brand"
                value={brandSearchQuery}
                onChange={(e) => setBrandSearchQuery(e.target.value)}
                helperText={
                  selectedBrand
                    ? `Selected: ${selectedBrand}`
                    : "Type to search brands"
                }
                InputProps={{
                  endAdornment: brandsFetching ? (
                    <CircularProgress size={20} />
                  ) : undefined,
                }}
              />
              {brandSearchQuery && filteredBrands.length > 0 ? (
                <Paper
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 1300,
                    maxHeight: 200,
                    overflow: "auto",
                    mt: 1,
                  }}
                >
                  <List dense>
                    {filteredBrands.slice(0, 10).map((brand) => (
                      <ListItemButton
                        key={brand._id}
                        onClick={() => handleBrandSelect(brand._id, brand.name)}
                        selected={selectedBrandId === brand._id}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight="bold">
                              {brand.name}
                            </Typography>
                          }
                          secondary={
                            brand.aliases && brand.aliases.length > 0 ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontStyle: "italic" }}
                              >
                                Aliases: {brand.aliases.join(", ")}
                              </Typography>
                            ) : null
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              ) : null}
            </Box>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!canSave}>
          {saveLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const splitProgramCsv = (value: string): string[] =>
  value
    .split(/[,;\n]+/)
    .map((part) => part.trim())
    .filter(Boolean);

// Program Edit Dialog Component
const ProgramEditDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  contentId: string;
  contentTitle: string;
  currentProgramId: string | undefined;
  programs: { _id: string; name: string }[];
  onSave: (contentId: string, programId: string | null) => Promise<void>;
  onCreateAndSave: (
    contentId: string,
    payload: { name: string; keywords: string[] },
  ) => Promise<void>;
  saveInProgress: boolean;
}> = ({
  open,
  onClose,
  contentId,
  contentTitle,
  currentProgramId,
  programs,
  onSave,
  onCreateAndSave,
  saveInProgress,
}) => {
  const [selectedProgramId, setSelectedProgramId] = useState<string>("");
  const [createMode, setCreateMode] = useState(false);
  const [newName, setNewName] = useState("");
  const [newKeywords, setNewKeywords] = useState("");

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelectedProgramId(currentProgramId || "");
      setCreateMode(false);
      setNewName("");
      setNewKeywords("");
    }
  }, [open, currentProgramId]);

  const handleToggleCreateMode = () => {
    setCreateMode((prev) => {
      if (prev) {
        setNewName("");
        setNewKeywords("");
      }
      return !prev;
    });
  };

  const handleSave = () => {
    if (createMode) {
      const name = newName.trim();
      if (!name) return;
      onCreateAndSave(contentId, {
        name,
        keywords: splitProgramCsv(newKeywords),
      });
      return;
    }
    const programId = selectedProgramId === "" ? null : selectedProgramId;
    onSave(contentId, programId);
  };

  const handleCancel = () => {
    onClose();
  };

  const canSave = createMode
    ? Boolean(newName.trim())
    : selectedProgramId !== (currentProgramId || "");

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Program: {contentTitle}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 2 }}>
          {!createMode ? (
            <>
              <Typography variant="body2" color="text.secondary">
                Select a program from the list below. All programs for this
                podcast channel are available.
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Program</InputLabel>
                <Select
                  value={selectedProgramId}
                  label="Program"
                  onChange={(e) => setSelectedProgramId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>No Program</em>
                  </MenuItem>
                  {programs.map((program) => (
                    <MenuItem key={program._id} value={program._id}>
                      {program.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {programs.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                >
                  No programs available for this podcast channel.
                </Typography>
              )}
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                Create a new program for this podcast, then assign it to this
                episode.
              </Typography>
              <TextField
                label="Program name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                fullWidth
                required
                size="small"
                autoFocus
              />
              <TextField
                label="Keywords (comma-separated)"
                value={newKeywords}
                onChange={(e) => setNewKeywords(e.target.value)}
                fullWidth
                size="small"
              />
            </>
          )}
          <Button
            size="small"
            onClick={handleToggleCreateMode}
            disabled={saveInProgress}
            sx={{ alignSelf: "flex-start" }}
          >
            {createMode
              ? "Choose existing program"
              : "Create new program…"}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={saveInProgress}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saveInProgress || !canSave}
        >
          {saveInProgress ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              {createMode ? "Creating..." : "Updating..."}
            </>
          ) : createMode ? (
            "Create & Assign"
          ) : (
            "Update Program"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BulkProgramAssignDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  selectedCount: number;
  programs: { _id: string; name: string }[];
  showDigitalOption: boolean;
  onAssign: (programId: string) => Promise<void>;
  onCreateAndAssign: (payload: {
    name: string;
    keywords: string[];
    isDigital: boolean;
  }) => Promise<void>;
  saveInProgress: boolean;
}> = ({
  open,
  onClose,
  selectedCount,
  programs,
  showDigitalOption,
  onAssign,
  onCreateAndAssign,
  saveInProgress,
}) => {
  const [selectedProgramId, setSelectedProgramId] = useState("");
  const [createMode, setCreateMode] = useState(false);
  const [newName, setNewName] = useState("");
  const [newKeywords, setNewKeywords] = useState("");
  const [newIsDigital, setNewIsDigital] = useState(false);

  React.useEffect(() => {
    if (open) {
      setSelectedProgramId("");
      setCreateMode(programs.length === 0);
      setNewName("");
      setNewKeywords("");
      setNewIsDigital(false);
    }
  }, [open, programs.length]);

  const canSave = createMode
    ? Boolean(newName.trim())
    : Boolean(selectedProgramId);

  const handleSave = () => {
    if (createMode) {
      const name = newName.trim();
      if (!name) return;
      onCreateAndAssign({
        name,
        keywords: splitProgramCsv(newKeywords),
        isDigital: showDigitalOption && newIsDigital,
      });
      return;
    }
    if (selectedProgramId) onAssign(selectedProgramId);
  };

  const noun = selectedCount === 1 ? "video" : "videos";

  return (
    <Dialog
      open={open}
      onClose={saveInProgress ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Assign program to {selectedCount} {noun}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 2 }}>
          {!createMode ? (
            <Autocomplete
              options={programs}
              getOptionLabel={(p) => p.name}
              value={programs.find((p) => p._id === selectedProgramId) ?? null}
              onChange={(_e, value) => setSelectedProgramId(value?._id ?? "")}
              isOptionEqualToValue={(a, b) => a._id === b._id}
              renderInput={(params) => (
                <TextField {...params} label="Program" size="small" autoFocus />
              )}
              noOptionsText="No programs on this channel"
            />
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                Create a new program for this channel, then assign it to the
                selected {noun}.
              </Typography>
              <TextField
                label="Program name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                fullWidth
                required
                size="small"
                autoFocus
              />
              <TextField
                label="Keywords (comma-separated)"
                value={newKeywords}
                onChange={(e) => setNewKeywords(e.target.value)}
                fullWidth
                size="small"
              />
              {showDigitalOption && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newIsDigital}
                      onChange={(e) => setNewIsDigital(e.target.checked)}
                    />
                  }
                  label="100% digital"
                />
              )}
            </>
          )}
          <Button
            size="small"
            onClick={() => setCreateMode((prev) => !prev)}
            disabled={saveInProgress}
            sx={{ alignSelf: "flex-start" }}
          >
            {createMode ? "Choose existing program" : "Create new program…"}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saveInProgress}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saveInProgress || !canSave}
        >
          {saveInProgress ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Saving...
            </>
          ) : createMode ? (
            "Create & Assign"
          ) : (
            "Assign"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


const PodcastContentAnalysis: React.FC = () => {
  const { podcastId } = useParams<{ podcastId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expandedContent, setExpandedContent] = useState<Set<string>>(
    new Set(),
  );
  const [brandTab, setBrandTab] = useState<BrandTab>("valid");
  const [selectedBrandName, setSelectedBrandName] = useState<string | null>(
    null,
  );
  const pendingBrandScrollRef = React.useRef<string | null>(null);
  const prevBrandFilterRef = React.useRef<string | null>(null);
  // Use Zustand store for persistent filters
  const {
    selectedWeek,
    setSelectedWeek,
    reviewDateFilterMode,
    setReviewDateFilterMode,
    reviewStatusFilter,
    setReviewStatusFilter,
    selectedProgram,
    setSelectedProgram,
    brandSearchQuery,
    setBrandSearchQuery,
  } = useFilterStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [contentPerPage] = useState(20);
  const [titleSearch, setTitleSearch] = useState("");
  const debouncedTitleSearch = useDebounce(titleSearch.trim(), 600);
  const [digitalFilter, setDigitalFilter] = useState<
    "all" | "digital" | "non-digital"
  >("all");
  const [transcribedFilter, setTranscribedFilter] = useState<
    "all" | "yes" | "no"
  >("all");
  const [analysisFilter, setAnalysisFilter] = useState<
    "all" | "analyzed" | "not-analyzed"
  >("all");
  const [selectedContentIds, setSelectedContentIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [bulkProgramDialogOpen, setBulkProgramDialogOpen] = useState(false);
  const [bulkProgramSaveInProgress, setBulkProgramSaveInProgress] =
    useState(false);
  const [aliasDialogOpen, setAliasDialogOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<{
    name: string;
    id?: string;
    aliases: string[];
    mentionCount?: number;
  } | null>(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success",
  );

  // Episode mention review dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [mentionReviewSession, setMentionReviewSession] =
    useState<MentionReviewSession | null>(null);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [dialogSyncedMentions, setDialogSyncedMentions] = useState<
    Map<string, BrandMention>
  >(() => new Map());

  const upsertDialogSyncedMention = useCallback((mention: BrandMention) => {
    setDialogSyncedMentions((prev) => {
      const next = new Map(prev);
      next.set(mention._id, mention);
      return next;
    });
  }, []);

  const removeDialogSyncedMention = useCallback((mentionId: string) => {
    setDialogSyncedMentions((prev) => {
      if (!prev.has(mentionId)) return prev;
      const next = new Map(prev);
      next.delete(mentionId);
      return next;
    });
  }, []);

  const syncMentionToEpisodeRow = useCallback(
    (contentId: string, mention: BrandMention) => {
      syncContentMentionsInCache(queryClient, contentId, (mentions) => {
        const idx = mentions.findIndex((m) => m._id === mention._id);
        if (idx >= 0) {
          return mentions.map((m) => (m._id === mention._id ? mention : m));
        }
        return [...mentions, mention].sort((a, b) => a.startTime - b.startTime);
      });
      upsertDialogSyncedMention(mention);
    },
    [queryClient, upsertDialogSyncedMention],
  );


  // Program edit dialog state
  const [programEditDialogOpen, setProgramEditDialogOpen] = useState(false);
  const [programEditContentId, setProgramEditContentId] = useState<string>("");
  const [programEditContentTitle, setProgramEditContentTitle] =
    useState<string>("");
  const [programEditCurrentProgramId, setProgramEditCurrentProgramId] =
    useState<string | undefined>(undefined);
  const [programEditSaveInProgress, setProgramEditSaveInProgress] =
    useState(false);

  const reviewWeekOptions = React.useMemo(() => buildWeekOptions(), []);
  const reviewMonthOptions = React.useMemo(() => buildMonthOptions(), []);

  // Reset page, expanded cards, and brand filter when filters change
  React.useEffect(() => {
    setCurrentPage(1);
    setExpandedContent(new Set());
    setSelectedBrandName(null);
  }, [
    selectedWeek,
    reviewDateFilterMode,
    selectedProgram,
    reviewStatusFilter,
    debouncedTitleSearch,
    digitalFilter,
    transcribedFilter,
    analysisFilter,
  ]);

  React.useEffect(() => {
    setSelectedContentIds(new Set());
    setDigitalFilter("all");
    setTranscribedFilter("all");
    setAnalysisFilter("all");
  }, [podcastId]);

  React.useEffect(() => {
    setExpandedContent(new Set());
    setSelectedBrandName(null);
    pendingBrandScrollRef.current = null;
  }, [currentPage]);

  // Fetch podcast details
  const {
    data: podcastData,
    isLoading: podcastLoading,
    error: podcastError,
  } = useQuery({
    queryKey: ["podcast", podcastId],
    queryFn: () => apiService.getPodcast(podcastId!),
    enabled: !!podcastId,
  });

  // Fetch programs for the podcast (with review stats when date filter is set)
  const weekParamForApi =
    selectedWeek !== "all" ? selectedWeek : undefined;

  const { data: programsData } = useQuery({
    queryKey: ["podcast-programs", podcastId],
    queryFn: () => apiService.getPodcastPrograms(podcastId!),
    enabled: !!podcastId,
  });

  // Fetch paginated content analysis
  const {
    data: contentData,
    isLoading: contentLoading,
    isPlaceholderData: contentIsPlaceholder,
    isFetching: contentFetching,
    error: contentError,
  } = useQuery({
    queryKey: [
      "podcast-content-analysis",
      podcastId,
      currentPage,
      selectedWeek,
      reviewDateFilterMode,
      selectedProgram,
      reviewStatusFilter,
      debouncedTitleSearch,
      digitalFilter,
      transcribedFilter,
      analysisFilter,
    ],
    queryFn: () => {
      return apiService.getPodcastContentAnalysis(
        podcastId!,
        currentPage,
        contentPerPage,
        undefined,
        "publishedAt",
        "asc",
        weekParamForApi,
        selectedProgram !== "all" ? selectedProgram : undefined,
        reviewStatusFilter !== "all" ? reviewStatusFilter : undefined,
        debouncedTitleSearch || undefined,
        digitalFilter !== "all" ? digitalFilter : undefined,
        transcribedFilter !== "all" ? transcribedFilter : undefined,
        analysisFilter !== "all" ? analysisFilter : undefined,
      );
    },
    enabled: !!podcastId,
    // Keep the current list on screen while a new filter/search loads (same podcast only).
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === podcastId ? previousData : undefined,
  });
  const contentRefetching = contentFetching && contentIsPlaceholder;

  const pageContent = contentData?.content || [];

  const {
    isLoading: mentionsLoading,
    validBrandCount,
    invalidBrandCount,
    aggregateBrands,
    getContentIdsForBrand,
    contentHasBrand,
    getMentionsForContent,
    getMentionsLoadingForContent,
  } = usePageBrandMentions(pageContent);

  const aggregatedBrands = React.useMemo(
    () => aggregateBrands(brandTab, brandSearchQuery),
    [aggregateBrands, brandTab, brandSearchQuery],
  );

  const visibleContent = React.useMemo(() => {
    if (!selectedBrandName) return pageContent;
    return pageContent.filter((item) =>
      contentHasBrand(item._id, selectedBrandName, brandTab),
    );
  }, [pageContent, selectedBrandName, brandTab, contentHasBrand]);

  const episodeMentionsMap = React.useMemo(() => {
    const map = new Map<string, BrandMention[]>();
    for (const item of visibleContent) {
      const contentIdStr = contentIdForApi(item._id);
      map.set(
        contentIdStr,
        getMentionsForContent(item._id, selectedBrandName),
      );
    }
    return map;
  }, [visibleContent, selectedBrandName, getMentionsForContent]);

  React.useEffect(() => {
    if (!selectedBrandName) return;

    const filterKey = `${brandTab}:${selectedBrandName}`;
    const ids = getContentIdsForBrand(selectedBrandName, brandTab);
    if (ids.length === 0) return;

    // Auto-expand only when the brand filter changes, not on every mention refetch.
    if (prevBrandFilterRef.current === filterKey) return;
    prevBrandFilterRef.current = filterKey;
    setExpandedContent(new Set(ids));
  }, [selectedBrandName, brandTab, getContentIdsForBrand]);

  React.useEffect(() => {
    if (selectedBrandName) return;
    prevBrandFilterRef.current = null;
  }, [selectedBrandName]);

  // Scroll once when a brand is selected and matching episodes are available.
  React.useEffect(() => {
    if (!pendingBrandScrollRef.current || !selectedBrandName) return;
    const scrollKey = `${brandTab}:${selectedBrandName}`;
    if (pendingBrandScrollRef.current !== scrollKey) return;

    const ids = getContentIdsForBrand(selectedBrandName, brandTab);
    if (ids.length === 0) return;

    pendingBrandScrollRef.current = null;
    requestAnimationFrame(() => {
      document
        .querySelector(`[data-content-id="${ids[0]}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }, [selectedBrandName, brandTab, getContentIdsForBrand]);

  const handleBrandSelect = (brandName: string | null) => {
    if (brandName) {
      pendingBrandScrollRef.current = `${brandTab}:${brandName}`;
    } else {
      pendingBrandScrollRef.current = null;
      prevBrandFilterRef.current = null;
      setExpandedContent(new Set());
    }
    setSelectedBrandName(brandName);
  };

  const handleBrandTabChange = (tab: BrandTab) => {
    setBrandTab(tab);
    setSelectedBrandName(null);
    pendingBrandScrollRef.current = null;
    prevBrandFilterRef.current = null;
    setExpandedContent(new Set());
  };

  const handleExpandAll = () => {
    setExpandedContent(
      new Set(visibleContent.map((item) => contentIdForApi(item._id))),
    );
  };

  const handleCollapseAll = () => {
    setExpandedContent(new Set());
  };

  const handleMentionsChanged = () => {
    queryClient.invalidateQueries({
      queryKey: ["podcast-content-analysis-mentions"],
    });
    queryClient.invalidateQueries({ queryKey: ["podcast-content-analysis"] });
    queryClient.invalidateQueries({ queryKey: ["podcast-analysis-summary"] });
  };

  // Callback handlers for content item updates
  const handleMentionDeleted = (contentId: string, mentionId: string) => {
    // This will be handled by the ContentItemCard component
    console.log(`Mention ${mentionId} deleted from content ${contentId}`);
  };

  const handleMentionUpdated = (contentId: string, mention: BrandMention) => {
    // This will be handled by the ContentItemCard component
    console.log(`Mention ${mention._id} updated in content ${contentId}`);
  };

  const handleMentionFeedback = (
    contentId: string,
    mentionId: string,
    status: 'rejected' | null,
    reason?: string,
    context?: string,
  ) => {
    // Handled locally by ContentItemCard; log for debugging
    console.log(
      `Mention ${mentionId} feedback updated: ${status ?? 'cleared'}${reason ? ` (${reason})` : ''}${context ? ` [${context}]` : ''} in content ${contentId}`,
    );
  };

  const handleMentionClassificationChange = (contentId: string, mentionId: string, mentionType?: string, placementType?: string, visualCue?: VisualCue | null) => {
    console.log(`Mention ${mentionId} classification updated: type=${mentionType ?? '-'} placement=${placementType ?? '-'} visualCue=${visualCue ?? '-'} in content ${contentId}`);
  };

  const toggleContentExpansion = (contentId: string) => {
    const idStr = contentIdForApi(contentId);
    const newExpanded = new Set(expandedContent);
    if (newExpanded.has(idStr)) {
      newExpanded.delete(idStr);
    } else {
      newExpanded.add(idStr);
    }
    setExpandedContent(newExpanded);
  };

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const getClassificationColor = (classification: string): string => {
    switch (classification) {
      case "paid":
        return "#f44336";
      case "organic":
        return "#4caf50";
      case "sponsor":
        return "#ff9800";
      default:
        return "#9e9e9e";
    }
  };

  const createYouTubeUrlWithTimestamp = (
    url: string,
    startTime: number,
  ): string => {
    if (!url) return "";

    try {
      // Handle different YouTube URL formats
      let videoId = "";

      if (url.includes("youtube.com/watch?v=")) {
        // Format: https://www.youtube.com/watch?v=VIDEO_ID
        const match = url.match(/[?&]v=([^&]+)/);
        videoId = match ? match[1] : "";
      } else if (url.includes("youtu.be/")) {
        // Format: https://youtu.be/VIDEO_ID
        const match = url.match(/youtu\.be\/([^?&]+)/);
        videoId = match ? match[1] : "";
      } else if (url.includes("youtube.com/embed/")) {
        // Format: https://www.youtube.com/embed/VIDEO_ID
        const match = url.match(/embed\/([^?&]+)/);
        videoId = match ? match[1] : "";
      }

      if (videoId) {
        return `https://www.youtube.com/watch?v=${videoId}&t=${Math.floor(startTime)}s`;
      }
    } catch (error) {
      console.error("Error parsing YouTube URL:", error);
    }

    return url;
  };

  const handleCopyMentionId = async (mentionId: string) => {
    try {
      await navigator.clipboard.writeText(mentionId);
      setSnackbarMessage("Mention ID copied to clipboard");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to copy mention ID:", error);
      setSnackbarMessage("Failed to copy mention ID");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const formatChileanDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-CL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getSourceIcon = (source: string) => {
    switch (source.toLowerCase()) {
      case "youtube":
        return <YouTubeIcon color="error" />;
      case "spotify":
        return <MusicNoteIcon color="success" />;
      default:
        return <PodcastIcon color="primary" />;
    }
  };

  const handleBrandManage = (
    brandName: string,
    brandId: string | undefined,
    aliases: string[] = [],
    mentionCount?: number,
  ) => {
    setSelectedBrand({ name: brandName, id: brandId, aliases, mentionCount });
    setAliasDialogOpen(true);
  };

  const handleAliasesUpdate = async (newAliases: string[]) => {
    if (!selectedBrand?.id) {
      setSnackbarMessage(
        "Cannot update aliases: this brand is not linked to a brand record yet.",
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      await apiService.updateBrandAliases(selectedBrand.id, newAliases);
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      queryClient.invalidateQueries({ queryKey: ["brand", selectedBrand.id] });
      queryClient.invalidateQueries({
        queryKey: ["podcast-content-analysis-mentions"],
      });

      setAliasDialogOpen(false);
      setSelectedBrand(null);

      setSnackbarMessage("Brand aliases updated successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to update brand aliases:", error);
      setSnackbarMessage("Failed to update brand aliases. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleAntiAliasesUpdate = async (newAntiAliases: string[]) => {
    if (!selectedBrand?.id) {
      setSnackbarMessage(
        "Cannot update anti-aliases: this brand is not linked to a brand record yet.",
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      await apiService.updateBrandAliases(
        selectedBrand.id,
        undefined,
        newAntiAliases,
      );
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      queryClient.invalidateQueries({ queryKey: ["brand", selectedBrand.id] });

      setAliasDialogOpen(false);
      setSelectedBrand(null);

      setSnackbarMessage("Brand anti-aliases updated successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to update brand anti-aliases:", error);
      setSnackbarMessage(
        "Failed to update brand anti-aliases. Please try again.",
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleCopyContentId = async (contentId: string) => {
    try {
      await navigator.clipboard.writeText(contentId);
      // You could add a toast notification here if you have one
      console.log("Content ID copied to clipboard:", contentId);
    } catch (err) {
      console.error("Failed to copy content ID:", err);
    }
  };

  const removeMentionFromReviewSession = (mentionId: string) => {
    let contentId: string | undefined;
    let updatedMentions: BrandMention[] = [];

    setMentionReviewSession((prev) => {
      if (!prev) return prev;
      contentId = prev.contentId;
      updatedMentions = prev.mentions.filter((m) => m._id !== mentionId);
      return { ...prev, mentions: updatedMentions };
    });

    return { contentId, updatedMentions };
  };

  const handleDeleteBrandMention = async (mentionId: string, hard = true) => {
    try {
      await apiService.deleteBrandMention(mentionId, hard);

      const { contentId: sessionContentId, updatedMentions } =
        removeMentionFromReviewSession(mentionId);

      let contentId = sessionContentId;
      if (!contentId) {
        for (const item of visibleContent) {
          const id = contentIdForApi(item._id);
          const mentions = episodeMentionsMap.get(id) ?? [];
          if (mentions.some((m: BrandMention) => m._id === mentionId)) {
            contentId = id;
            break;
          }
        }
      }

      if (contentId) {
        syncContentMentionsInCache(queryClient, contentId, (mentions) =>
          mentions.filter((m) => m._id !== mentionId),
        );
        removeDialogSyncedMention(mentionId);
      }

      if (contentId && updatedMentions.length > 0) {
        await syncContentReviewReadyFromMentions(contentId, updatedMentions);
      }

      queryClient.invalidateQueries({
        queryKey: ["podcast-content-analysis-mentions"],
      });
      queryClient.invalidateQueries({ queryKey: ["podcast-content-analysis"] });
      queryClient.invalidateQueries({ queryKey: ["podcast-analysis-summary"] });
      queryClient.invalidateQueries({ queryKey: ["brand-review-metrics"] });

      setSnackbarMessage("Brand mention deleted successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to delete brand mention:", error);
      setSnackbarMessage("Failed to delete brand mention. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      throw error;
    }
  };

  const handleRejectMentionInDialog = async (
    mentionId: string,
    reason?: string,
    context?: string,
  ) => {
    const { mention } = await apiService.updateMentionFeedback(
      mentionId,
      "rejected",
      reason,
      context,
    );
    const sessionMention = mentionReviewSession?.mentions.find(
      (m) => m._id === mentionId,
    );
    const mapped = mapApiMentionToBrandMention(
      mention as unknown as Record<string, unknown>,
      sessionMention,
    );
    const withRejectedFeedback: BrandMention = {
      ...mapped,
      feedback: {
        status: "rejected",
        reason: reason?.trim() || mapped.feedback?.reason,
        context: context?.trim() || mapped.feedback?.context,
        reviewedBy: mapped.feedback?.reviewedBy,
        reviewedAt: mapped.feedback?.reviewedAt,
      },
    };

    let contentId: string | undefined;
    let updatedMentions: BrandMention[] = [];

    setMentionReviewSession((prev) => {
      if (!prev) return prev;
      contentId = prev.contentId;
      updatedMentions = prev.mentions.map((m) =>
        m._id === mentionId ? withRejectedFeedback : m,
      );
      const active = updatedMentions.filter((m) => m.feedback?.status !== "rejected");
      if (active.length === 0) {
        setEditDialogOpen(false);
        return null;
      }
      return { ...prev, mentions: updatedMentions };
    });

    if (contentId) {
      syncMentionToEpisodeRow(contentId, withRejectedFeedback);
    }

    if (contentId && updatedMentions.length > 0) {
      await syncContentReviewReadyFromMentions(contentId, updatedMentions);
    }

    queryClient.invalidateQueries({
      queryKey: ["podcast-content-analysis-mentions"],
    });
    queryClient.invalidateQueries({ queryKey: ["podcast-content-analysis"] });
    queryClient.invalidateQueries({ queryKey: ["podcast-analysis-summary"] });
    queryClient.invalidateQueries({ queryKey: ["brand-review-metrics"] });

    setSnackbarMessage("Mention marked as not a mention");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleRestoreMentionInDialog = async (mentionId: string) => {
    await apiService.updateMentionFeedback(mentionId, null);

    let contentId: string | undefined;
    let updatedMention: BrandMention | undefined;
    let updatedMentions: BrandMention[] = [];

    setMentionReviewSession((prev) => {
      if (!prev) return prev;
      contentId = prev.contentId;
      updatedMentions = prev.mentions.map((m) => {
        if (m._id !== mentionId) return m;
        updatedMention = { ...m, feedback: null };
        return updatedMention;
      });
      return { ...prev, mentions: updatedMentions };
    });

    if (contentId && updatedMention) {
      syncMentionToEpisodeRow(contentId, updatedMention);
    }

    if (contentId && updatedMentions.length > 0) {
      await syncContentReviewReadyFromMentions(contentId, updatedMentions);
    }

    queryClient.invalidateQueries({
      queryKey: ["podcast-content-analysis-mentions"],
    });
    queryClient.invalidateQueries({ queryKey: ["podcast-content-analysis"] });
    queryClient.invalidateQueries({ queryKey: ["podcast-analysis-summary"] });
    queryClient.invalidateQueries({ queryKey: ["brand-review-metrics"] });

    setSnackbarMessage("Mention restored");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleAddMissedMentionInDialog = async (
    sourceMention: BrandMention | null,
    edits: {
      brands: Array<{ brandName: string; brandId?: string }>;
      startTime: number;
      endTime: number;
      mentionType: string;
      placementType: BrandMention["placementType"];
      visualCue?: VisualCue | null;
    },
  ) => {
    if (!mentionReviewSession) {
      throw new Error("No active review session");
    }
    if (edits.brands.length === 0) {
      throw new Error("At least one brand is required");
    }

    const contentId = mentionReviewSession.contentId;
    const created: BrandMention[] = [];
    const mentionType = edits.mentionType || "paid";
    const placementType = edits.placementType || "content";
    const visualCue = edits.visualCue ?? null;

    for (const brand of edits.brands) {
      const { mention } = await apiService.createBrandMention({
        contentId,
        brandId: brand.brandId,
        brandName: brand.brandName,
        startTime: edits.startTime,
        endTime: edits.endTime,
        mentionType,
        placementType,
        commercialConfidence: sourceMention?.commercialConfidence,
        visualCue,
      });

      const mapped: BrandMention = {
        ...mapApiMentionToBrandMention(
          mention as unknown as Record<string, unknown>,
          sourceMention ?? undefined,
        ),
        classification: mentionType,
        placementType,
        visualCue,
      };
      created.push(mapped);
      syncMentionToEpisodeRow(contentId, mapped);
    }

    let updatedMentions: BrandMention[] = [];
    setMentionReviewSession((prev) => {
      if (!prev) return prev;
      updatedMentions = sortEpisodeMentions([...prev.mentions, ...created]);
      return { ...prev, mentions: updatedMentions };
    });

    await syncContentReviewReadyFromMentions(contentId, updatedMentions);

    queryClient.invalidateQueries({
      queryKey: ["podcast-content-analysis-mentions"],
    });
    queryClient.invalidateQueries({ queryKey: ["podcast-content-analysis"] });
    queryClient.invalidateQueries({ queryKey: ["brand-review-metrics"] });

    setSnackbarMessage(
      created.length > 1
        ? `${created.length} missed mentions added`
        : "Missed mention added",
    );
    setSnackbarSeverity("success");
    setSnackbarOpen(true);

    return created;
  };

  const handleDeleteMentionInDialog = async (mentionId: string) => {
    await handleDeleteBrandMention(mentionId, true);
  };

  const handleDeleteAllMentions = async (contentId: string) => {
    try {
      const result = await apiService.deleteAllContentMentions(contentId);

      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["podcast-content-analysis-mentions"] });
      queryClient.invalidateQueries({ queryKey: ["podcast-content-analysis"] });
      queryClient.invalidateQueries({ queryKey: ["podcast-analysis-summary"] });

      setSnackbarMessage(
        `Successfully deleted ${result.deletedMentions} brand mentions and reset analyzed status.`,
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to delete all brand mentions:", error);
      setSnackbarMessage(
        "Failed to delete all brand mentions. Please try again.",
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      throw error; // Re-throw to let the component handle the error state
    }
  };

  const handleUnlinkProgram = async (contentId: string) => {
    try {
      await apiService.unlinkProgramFromContent(contentId);

      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["podcast-content-analysis"] });

      setSnackbarMessage("Program unlinked successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to unlink program:", error);
      setSnackbarMessage("Failed to unlink program. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      throw error;
    }
  };

  const handleEditProgram = (
    contentId: string,
    contentTitle: string,
    currentProgramId: string | undefined,
  ) => {
    setProgramEditContentId(contentId);
    setProgramEditContentTitle(contentTitle);
    setProgramEditCurrentProgramId(currentProgramId);
    setProgramEditDialogOpen(true);
  };

  const closeProgramEditDialog = () => {
    setProgramEditDialogOpen(false);
    setProgramEditContentId("");
    setProgramEditContentTitle("");
    setProgramEditCurrentProgramId(undefined);
  };

  const applyProgramAssignmentLocally = (
    contentId: string,
    programId: string | null,
    programName: string | undefined,
  ) => {
    setMentionReviewSession((prev) =>
      prev && contentIdForApi(prev.contentId) === contentIdForApi(contentId)
        ? {
            ...prev,
            programId: programId ?? undefined,
            programName,
          }
        : prev,
    );
  };

  const handleSaveProgram = async (
    contentId: string,
    programId: string | null,
  ) => {
    setProgramEditSaveInProgress(true);
    try {
      await apiService.linkProgramToContent(contentId, programId);

      queryClient.invalidateQueries({ queryKey: ["podcast-content-analysis"] });

      const programName = programId
        ? programsData?.programs?.find((p) => p._id === programId)?.name
        : undefined;

      applyProgramAssignmentLocally(contentId, programId, programName);

      setSnackbarMessage(
        programId
          ? "Program linked successfully"
          : "Program unlinked successfully",
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      closeProgramEditDialog();
    } catch (error) {
      console.error("Failed to update program:", error);
      setSnackbarMessage("Failed to update program. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setProgramEditSaveInProgress(false);
    }
  };

  const handleCreateAndSaveProgram = async (
    contentId: string,
    payload: { name: string; keywords: string[] },
  ) => {
    if (!podcastId) return;
    setProgramEditSaveInProgress(true);
    try {
      const { program } = await apiService.createAdminChannelProgram(
        podcastId,
        {
          name: payload.name,
          keywords: payload.keywords.length > 0 ? payload.keywords : undefined,
        },
      );
      const newProgramId = String(program._id);
      await apiService.linkProgramToContent(contentId, newProgramId);

      queryClient.invalidateQueries({ queryKey: ["podcast-content-analysis"] });
      queryClient.invalidateQueries({
        queryKey: ["podcast-programs", podcastId],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-channel-programs", podcastId],
      });

      applyProgramAssignmentLocally(contentId, newProgramId, program.name);

      setSnackbarMessage(`Program "${program.name}" created and assigned`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      closeProgramEditDialog();
    } catch (error) {
      console.error("Failed to create and assign program:", error);
      setSnackbarMessage(
        "Failed to create program. Please try again.",
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setProgramEditSaveInProgress(false);
    }
  };

  const handleContentSelectChange = useCallback(
    (contentId: string, selected: boolean) => {
      setSelectedContentIds((prev) => {
        const next = new Set(prev);
        if (selected) next.add(contentId);
        else next.delete(contentId);
        return next;
      });
    },
    [],
  );

  const clearContentSelection = () => setSelectedContentIds(new Set());

  const finishBulkProgramUpdate = (
    ids: string[],
    programId: string | null,
    programName: string | undefined,
    modifiedCount: number,
  ) => {
    for (const id of ids) {
      applyProgramAssignmentLocally(id, programId, programName);
    }
    queryClient.invalidateQueries({ queryKey: ["podcast-content-analysis"] });
    queryClient.invalidateQueries({ queryKey: ["podcast-programs", podcastId] });
    queryClient.invalidateQueries({
      queryKey: ["admin-channel-programs", podcastId],
    });
    const noun = modifiedCount === 1 ? "video" : "videos";
    setSnackbarMessage(
      programId
        ? `Program "${programName ?? ""}" assigned to ${modifiedCount} ${noun}`
        : `Program removed from ${modifiedCount} ${noun}`,
    );
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setSelectedContentIds(new Set());
    setBulkProgramDialogOpen(false);
  };

  const handleBulkAssignProgram = async (programId: string | null) => {
    if (!podcastId || selectedContentIds.size === 0) return;
    const ids = Array.from(selectedContentIds);
    setBulkProgramSaveInProgress(true);
    try {
      const result = await apiService.bulkAssignProgramToContent(
        podcastId,
        ids,
        programId,
      );
      const programName = programId
        ? programsData?.programs?.find((p) => p._id === programId)?.name
        : undefined;
      finishBulkProgramUpdate(ids, programId, programName, result.modifiedCount);
    } catch (error) {
      console.error("Failed to bulk update program:", error);
      setSnackbarMessage("Failed to update programs. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setBulkProgramSaveInProgress(false);
    }
  };

  const handleBulkCreateAndAssignProgram = async (payload: {
    name: string;
    keywords: string[];
    isDigital: boolean;
  }) => {
    if (!podcastId || selectedContentIds.size === 0) return;
    const ids = Array.from(selectedContentIds);
    setBulkProgramSaveInProgress(true);
    try {
      const { program } = await apiService.createAdminChannelProgram(podcastId, {
        name: payload.name,
        keywords: payload.keywords.length > 0 ? payload.keywords : undefined,
        isDigital: payload.isDigital || undefined,
      });
      const newProgramId = String(program._id);
      const result = await apiService.bulkAssignProgramToContent(
        podcastId,
        ids,
        newProgramId,
      );
      finishBulkProgramUpdate(ids, newProgramId, program.name, result.modifiedCount);
    } catch (error: any) {
      console.error("Failed to create and bulk assign program:", error);
      setSnackbarMessage(
        error?.response?.data?.error || "Failed to create program. Please try again.",
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setBulkProgramSaveInProgress(false);
    }
  };

  const handleReviewReadyChange = async (
    contentId: string,
    ready: boolean,
  ) => {
    const contentIdStr = contentIdForApi(contentId);
    const dropsFromCurrentFilter =
      (reviewStatusFilter === "pending" && ready) ||
      (reviewStatusFilter === "ready" && !ready);

    try {
      const result = await apiService.setContentReviewReady(contentIdStr, ready);

      queryClient.setQueriesData(
        { queryKey: ["podcast-content-analysis"] },
        (
          old:
            | {
                content?: ContentItem[];
                pagination?: {
                  total: number;
                  pages: number;
                  page: number;
                  limit: number;
                };
              }
            | undefined,
        ) => {
          if (!old?.content) return old;

          if (dropsFromCurrentFilter) {
            const nextContent = old.content.filter(
              (item) => contentIdForApi(item._id) !== contentIdStr,
            );
            const removedCount = old.content.length - nextContent.length;
            return {
              ...old,
              content: nextContent,
              pagination: old.pagination
                ? {
                    ...old.pagination,
                    total: Math.max(0, old.pagination.total - removedCount),
                  }
                : old.pagination,
            };
          }

          return {
            ...old,
            content: old.content.map((item) =>
              contentIdForApi(item._id) === contentIdStr
                ? {
                    ...item,
                    reviewReadyAt: result.reviewReadyAt ?? undefined,
                  }
                : item,
            ),
          };
        },
      );

      setSnackbarMessage(
        ready ? "Episode marked as ready" : "Episode marked as pending",
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      // Refetching a filtered list right after PATCH can briefly restore stale rows.
      if (!dropsFromCurrentFilter) {
        queryClient.invalidateQueries({ queryKey: ["podcast-content-analysis"] });
      }
      queryClient.invalidateQueries({ queryKey: ["podcast-analysis-summary"] });

      return result;
    } catch (error) {
      console.error("Failed to update review ready status:", error);
      setSnackbarMessage("Failed to update review status. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      throw error;
    }
  };

  const syncContentReviewReadyFromMentions = async (
    contentId: string,
    mentions: BrandMention[],
  ) => {
    if (mentions.length === 0) return;

    const contentItem = pageContent.find(
      (c) => contentIdForApi(c._id) === contentId,
    );
    const isContentReady = !!contentItem?.reviewReadyAt;
    const allMentionsReady = areAllReviewMentionsReady(mentions);

    if (allMentionsReady && !isContentReady) {
      await handleReviewReadyChange(contentId, true);
    } else if (!allMentionsReady && isContentReady) {
      await handleReviewReadyChange(contentId, false);
    }
  };

  const handleDuplicateMention = async (mention: BrandMention) => {
    try {
      const {
        _id,
        context,
        feedback,
        transcriptExcerpt,
        review,
        aiClassification,
        ...mentionData
      } = mention;
      const result = await apiService.createPrivateBrandMention({
        ...mentionData,
        context: "",
      });
      const newMention = mapCreatedMentionToBrandMention(
        result.mention,
        mention,
      );

      let updatedMentions: BrandMention[] = [];

      setMentionReviewSession((prev) => {
        if (!prev || prev.contentId !== mention.contentId) return prev;
        updatedMentions = sortEpisodeMentions([...prev.mentions, newMention]);
        return {
          ...prev,
          mentions: updatedMentions,
        };
      });

      if (updatedMentions.length > 0) {
        await syncContentReviewReadyFromMentions(mention.contentId, updatedMentions);
      }

      syncMentionToEpisodeRow(contentIdForApi(mention.contentId), newMention);

      queryClient.invalidateQueries({
        queryKey: ["podcast-content-analysis-mentions"],
      });
      queryClient.invalidateQueries({ queryKey: ["podcast-content-analysis"] });
      queryClient.invalidateQueries({ queryKey: ["podcast-analysis-summary"] });

      setSnackbarMessage("Brand mention duplicated successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error("Error duplicating mention:", error);
      setSnackbarMessage(
        error.response?.data?.error ||
          "Failed to duplicate mention. Please try again.",
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const openMentionReview = (
    contentItem: ContentItem,
    mentions: BrandMention[],
    opts?: { mentionId?: string; startAtFirst?: boolean },
  ) => {
    const sorted = sortAllEpisodeMentions(mentions);
    const reviewable = sortEpisodeMentions(mentions);

    const initialMentionId = opts?.startAtFirst
      ? reviewable[0]?._id
      : opts?.mentionId;
    const initialIndex = initialMentionId
      ? sorted.findIndex((m) => m._id === initialMentionId)
      : 0;
    const safeIndex =
      sorted.length === 0 ? 0 : initialIndex >= 0 ? initialIndex : 0;

    setMentionReviewSession({
      contentId: contentIdForApi(contentItem._id),
      contentUrl: contentItem.url,
      contentTitle: contentItem.title,
      mentions: sorted,
      initialIndex: safeIndex,
      initialMentionId: initialMentionId ?? sorted[safeIndex]?._id,
      reviewReadyAt: contentItem.reviewReadyAt,
      programId: contentItem.program?._id,
      programName: contentItem.program?.name,
    });
    setEditDialogOpen(true);
  };

  const handleReviewEpisodeMentions = (contentItem: ContentItem) => {
    const mentions = episodeMentionsMap.get(contentIdForApi(contentItem._id)) ?? [];
    openMentionReview(contentItem, mentions, { startAtFirst: true });
  };

  const handleNavigateReviewEpisode = React.useCallback(
    (direction: -1 | 1) => {
      if (!mentionReviewSession) return;
      const currentIdx = visibleContent.findIndex(
        (item) =>
          contentIdForApi(item._id) === mentionReviewSession.contentId,
      );
      if (currentIdx < 0) return;
      const nextItem = visibleContent[currentIdx + direction];
      if (!nextItem) return;

      const nextId = contentIdForApi(nextItem._id);
      setExpandedContent((prev) => {
        const next = new Set(prev);
        next.add(nextId);
        return next;
      });

      const mentions = episodeMentionsMap.get(nextId) ?? [];
      openMentionReview(nextItem, mentions, { startAtFirst: true });
    },
    [
      mentionReviewSession,
      visibleContent,
      episodeMentionsMap,
    ],
  );

  const reviewEpisodeNav = React.useMemo(() => {
    if (!mentionReviewSession || visibleContent.length === 0) return null;
    const index = visibleContent.findIndex(
      (item) => contentIdForApi(item._id) === mentionReviewSession.contentId,
    );
    if (index < 0) return null;
    return {
      index,
      total: visibleContent.length,
      hasPrev: index > 0,
      hasNext: index < visibleContent.length - 1,
      onNavigate: handleNavigateReviewEpisode,
    };
  }, [mentionReviewSession, visibleContent, handleNavigateReviewEpisode]);

  const handleEditMention = (mention: BrandMention) => {
    const mentionContentId = contentIdForApi(mention.contentId);
    const contentItem = pageContent.find(
      (c) => contentIdForApi(c._id) === mentionContentId,
    );
    if (!contentItem) return;
    const mentions = episodeMentionsMap.get(mentionContentId) ?? [];
    openMentionReview(contentItem, mentions, { mentionId: mention._id });
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setMentionReviewSession(null);
  };

  const advanceToNextReviewEpisodeAfterReady = useCallback(
    (contentId: string) => {
      const contentIdStr = contentIdForApi(contentId);
      const currentIdx = visibleContent.findIndex(
        (item) => contentIdForApi(item._id) === contentIdStr,
      );
      if (currentIdx < 0) return;

      const dropsFromCurrentFilter =
        reviewStatusFilter === "pending" ||
        reviewStatusFilter === "pending-mentions";
      const nextContent = dropsFromCurrentFilter
        ? visibleContent.filter(
            (item) => contentIdForApi(item._id) !== contentIdStr,
          )
        : visibleContent;
      const nextIdx = dropsFromCurrentFilter ? currentIdx : currentIdx + 1;
      const nextItem = nextContent[nextIdx];

      if (!nextItem) {
        handleEditDialogClose();
        return;
      }

      const nextId = contentIdForApi(nextItem._id);
      setExpandedContent((prev) => {
        const next = new Set(prev);
        next.add(nextId);
        return next;
      });

      const mentions = episodeMentionsMap.get(nextId) ?? [];
      openMentionReview(nextItem, mentions, { startAtFirst: true });
    },
    [visibleContent, reviewStatusFilter, episodeMentionsMap],
  );

  const handleMentionReadyInDialog = async (mentionId: string, ready: boolean) => {
    const sessionMention = mentionReviewSession?.mentions.find(
      (m) => m._id === mentionId,
    );

    try {
      const wasRejected = sessionMention?.feedback?.status === "rejected";

      if (ready && wasRejected) {
        await apiService.updateMentionFeedback(mentionId, null);
      }

      const { mention: rawMention } = await apiService.updateMentionFeedback(
        mentionId,
        ready ? "confirmed" : null,
      );
      const mapped = mapApiMentionToBrandMention(
        rawMention as unknown as Record<string, unknown>,
        sessionMention,
      );
      const updatedMentionFromApi: BrandMention = ready
        ? {
            ...mapped,
            feedback: {
              status: "confirmed",
              reason: mapped.feedback?.reason,
              reviewedBy: mapped.feedback?.reviewedBy,
              reviewedAt: mapped.feedback?.reviewedAt,
            },
          }
        : { ...mapped, feedback: null };

      let contentId: string | undefined;
      let updatedMentions: BrandMention[] = [];

      setMentionReviewSession((prev) => {
        if (!prev) return null;
        contentId = prev.contentId;
        updatedMentions = prev.mentions.map((m) =>
          m._id === mentionId ? updatedMentionFromApi : m,
        );
        return { ...prev, mentions: updatedMentions };
      });

      if (contentId) {
        syncMentionToEpisodeRow(contentId, updatedMentionFromApi);
      }

      try {
        if (contentId && updatedMentions.length > 0) {
          await syncContentReviewReadyFromMentions(contentId, updatedMentions);
          if (ready && areAllReviewMentionsReady(updatedMentions)) {
            advanceToNextReviewEpisodeAfterReady(contentId);
          }
        }
      } catch (syncError) {
        console.error("Failed to sync episode ready status:", syncError);
      }

      queryClient.invalidateQueries({
        queryKey: ["podcast-content-analysis-mentions"],
      });

      setSnackbarMessage(
        ready ? "Mention marked as ready" : "Mention marked as pending",
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to update mention ready status:", error);
      setSnackbarMessage("Failed to mark mention ready. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      throw error;
    }
  };

  const handleSaveMentionTimes = async (
    mentionId: string,
    startTime: number,
    endTime: number,
    mentionType: string,
    commercialConfidence: number,
    brandName?: string,
    brandId?: string,
    visualCue?: VisualCue | null,
    placementType?: BrandMention["placementType"],
    saveSource: MentionSaveSource = "manual",
  ) => {
    setSaveInProgress(true);
    try {
      await apiService.updateMentionTimes(
        mentionId,
        startTime,
        endTime,
        mentionType,
        commercialConfidence,
        brandName,
        brandId,
        visualCue,
        placementType,
      );

      const contentId = mentionReviewSession?.contentId;
      const currentMention = mentionReviewSession?.mentions.find(
        (m) => m._id === mentionId,
      );

      let clearedRejection = false;
      if (
        (mentionType === "organic" || mentionType === "neutral") &&
        currentMention?.feedback?.status === "rejected"
      ) {
        await apiService.updateMentionFeedback(mentionId, null);
        clearedRejection = true;
      }

      const savedMention: BrandMention | undefined = currentMention
        ? {
            ...currentMention,
            startTime,
            endTime,
            classification: mentionType,
            commercialConfidence,
            brandName: brandName ?? currentMention.brandName,
            brandId: brandId ?? currentMention.brandId,
            visualCue: visualCue ?? null,
            placementType: placementType ?? currentMention.placementType,
            feedback: clearedRejection ? null : currentMention.feedback,
          }
        : undefined;

      setMentionReviewSession((prev) =>
        prev
          ? {
              ...prev,
              mentions: prev.mentions.map((m) =>
                m._id === mentionId && savedMention ? savedMention : m,
              ),
            }
          : null,
      );

      if (contentId && savedMention) {
        syncMentionToEpisodeRow(contentId, savedMention);
      }

      setSnackbarMessage(
        saveSource === "auto"
          ? "Changes saved automatically"
          : "Mention updated",
      );
      setSnackbarSeverity("success");

      queryClient.invalidateQueries({ queryKey: ["podcast-content-analysis-mentions"] });
      queryClient.invalidateQueries({ queryKey: ["podcast-content-analysis"] });
      queryClient.invalidateQueries({ queryKey: ["podcast-analysis-summary"] });

      setSnackbarOpen(true);
    } catch (error: any) {
      console.error("Error saving mention:", error);
      setSnackbarMessage(
        error.response?.data?.error ||
          "Failed to update mention. Please try again.",
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      throw error;
    } finally {
      setSaveInProgress(false);
    }
  };

  const handleMentionAutoSaved = () => {
    setSnackbarMessage("Changes saved automatically");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleAliasesSavedInDialog = (source: MentionSaveSource) => {
    setSnackbarMessage(
      source === "auto"
        ? "Changes saved automatically"
        : "Brand aliases updated successfully",
    );
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleBrandEdit = (
    brandName: string,
    brandId: string | undefined,
    aliases: string[],
    mentionCount: number,
  ) => {
    handleBrandManage(brandName, brandId, aliases, mentionCount);
  };

  const handleBulkSaveBrand = async (
    oldBrandName: string,
    newBrandId: string,
    newBrandName: string,
  ) => {
    try {
      // Step 1: Add the old brand name as an alias to the new brand
      try {
        // First, get the current aliases for the new brand
        const brandsData = await apiService.getBrands(1, 500);
        const targetBrand = brandsData.brands.find(
          (brand) => brand._id === newBrandId,
        );

        if (targetBrand) {
          const currentAliases = targetBrand.aliases || [];

          // Only add the alias if it doesn't already exist
          if (!currentAliases.includes(oldBrandName)) {
            const updatedAliases = [...currentAliases, oldBrandName];
            await apiService.updateBrandAliases(newBrandId, updatedAliases);
            console.log(
              `Added "${oldBrandName}" as alias to brand "${newBrandName}"`,
            );
          }
        }
      } catch (aliasError) {
        console.warn(
          "Failed to add brand alias, continuing with mention update:",
          aliasError,
        );
        // Don't fail the entire operation if alias update fails
      }

      // Step 2: Call API to bulk update all mentions of this brand in this podcast
      const result = await apiService.bulkUpdateBrandMentions(
        oldBrandName,
        newBrandId,
        newBrandName,
        podcastId,
      );

      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["podcast-content-analysis-mentions"] });
      queryClient.invalidateQueries({ queryKey: ["podcast-content-analysis"] });
      queryClient.invalidateQueries({ queryKey: ["podcast-analysis-summary"] });
      queryClient.invalidateQueries({ queryKey: ["brands"] });

      setSnackbarMessage(
        `Successfully updated ${result.updatedCount} mentions from "${oldBrandName}" to "${newBrandName}" and added "${oldBrandName}" as an alias`,
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      setAliasDialogOpen(false);
      setSelectedBrand(null);
    } catch (error: any) {
      console.error("Error bulk updating brand mentions:", error);
      setSnackbarMessage(
        error.response?.data?.error ||
          "Failed to update brand mentions. Please try again.",
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  if ((podcastLoading && !podcastData) || (contentLoading && !contentData)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (podcastError || contentError) {
    return (
      <Alert severity="error">
        Error loading podcast content analysis:{" "}
        {podcastError?.message || contentError?.message}
      </Alert>
    );
  }

  const podcast = podcastData;
  const pagination = contentData?.pagination;
  const showDigitalProgramOption =
    podcastData?.type === "tv" || podcastData?.category === "politics";
  const visibleContentIds = visibleContent.map((item) =>
    contentIdForApi(item._id),
  );
  const pageSelectedCount = visibleContentIds.filter((id) =>
    selectedContentIds.has(id),
  ).length;
  const pageAllSelected =
    visibleContentIds.length > 0 &&
    pageSelectedCount === visibleContentIds.length;
  const pageSomeSelected = pageSelectedCount > 0;

  const handleSelectPage = (checked: boolean) => {
    setSelectedContentIds((prev) => {
      const next = new Set(prev);
      for (const id of visibleContentIds) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  if (!podcast) {
    return <Alert severity="info">Podcast not found</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <Box display="flex" alignItems="center" minWidth={0}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/review")}
            sx={{ mr: 2 }}
          >
            Back to Review
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              queryClient.invalidateQueries({
                queryKey: ["podcast-content-analysis"],
              });
              queryClient.invalidateQueries({
                queryKey: ["podcast-all-content"],
              });
            }}
            sx={{ mr: 2 }}
          >
            🔄 Force Refresh
          </Button>
          <Avatar src={podcast.imageUrl} sx={{ width: 48, height: 48, mr: 2 }} />
          <Box>
            <Typography variant="h4" gutterBottom>
              {podcast.title}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Content Analysis
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AccountTreeIcon />}
          onClick={() => {
            if (!podcastId) return;
            navigate(`/review/${podcastId}/topics`);
          }}
          sx={{ flexShrink: 0 }}
        >
          Explore topics
        </Button>
      </Box>

      {/* Content List */}
      <Box>
        <Stack spacing={3}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { md: "center" },
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography variant="h6" sx={{ flexShrink: 0 }}>
              Content Items
              {selectedBrandName && (
                <Typography
                  component="span"
                  variant="body2"
                  color="primary"
                  sx={{ ml: 1 }}
                >
                  — {selectedBrandName} ({visibleContent.length} episodes)
                </Typography>
              )}
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              useFlexGap
              sx={{
                flexWrap: "wrap",
                minWidth: 0,
                flex: { md: "1 1 auto" },
              }}
            >
              <Box sx={{ flex: "1 1 220px", minWidth: 200 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search by title"
                  value={titleSearch}
                  onChange={(e) => setTitleSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: titleSearch || contentRefetching ? (
                      <InputAdornment position="end">
                        {contentRefetching && (
                          <CircularProgress size={16} sx={{ mr: 0.5 }} />
                        )}
                        {titleSearch && (
                        <IconButton
                          size="small"
                          aria-label="Clear title search"
                          onClick={() => setTitleSearch("")}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                        )}
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              </Box>
              <Box sx={{ flex: "0 0 auto", minWidth: 130 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Date Filter</InputLabel>
                  <Select
                    value={reviewDateFilterMode}
                    label="Date Filter"
                    MenuProps={reviewFilterSelectMenuProps}
                    onChange={(e) => {
                      setReviewDateFilterMode(
                        e.target.value as "week" | "month" | "day",
                      );
                      setSelectedWeek(
                        initialSelectedWeekForReviewMode(
                          e.target.value as "week" | "month" | "day",
                        ),
                      );
                      setCurrentPage(1);
                    }}
                  >
                    <MenuItem value="week">By Week</MenuItem>
                    <MenuItem value="month">By Month</MenuItem>
                    <MenuItem value="day">By Day</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: "0 0 auto", minWidth: 200 }}>
                {reviewDateFilterMode === "day" ? (
                  <ReviewDayRangePicker
                    value={selectedWeek}
                    onChange={(value) => {
                      setSelectedWeek(value);
                      setCurrentPage(1);
                    }}
                  />
                ) : (
                <FormControl fullWidth size="small">
                  <InputLabel>
                    {reviewDateFilterMode === "week" ? "Week" : "Month"}
                  </InputLabel>
                  <Select
                    value={selectedWeek}
                    label={reviewDateFilterMode === "week" ? "Week" : "Month"}
                    MenuProps={reviewFilterSelectMenuProps}
                    onChange={(e) => {
                      setSelectedWeek(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    {(reviewDateFilterMode === "week"
                      ? reviewWeekOptions
                      : reviewMonthOptions
                    ).map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                )}
              </Box>
              <Box sx={{ flex: "0 0 auto", minWidth: 160 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Program</InputLabel>
                  <Select
                    value={selectedProgram}
                    label="Program"
                    MenuProps={reviewFilterSelectMenuProps}
                    onChange={(e) => {
                      setSelectedProgram(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <MenuItem value="all">All Programs</MenuItem>
                    <MenuItem value="unassigned">
                      <em>No Program</em>
                    </MenuItem>
                    {(programsData?.programs || []).map((program) => (
                      <MenuItem key={program._id} value={program._id}>
                        {program.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {showDigitalProgramOption && (
                <Box sx={{ flex: "0 0 auto", minWidth: 160 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Digital</InputLabel>
                    <Select
                      value={digitalFilter}
                      label="Digital"
                      MenuProps={reviewFilterSelectMenuProps}
                      onChange={(e) =>
                        setDigitalFilter(
                          e.target.value as "all" | "digital" | "non-digital",
                        )
                      }
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="digital">100% digital</MenuItem>
                      <MenuItem value="non-digital">Not digital</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}
              <Box sx={{ flex: "0 0 auto", minWidth: 160 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Review Status</InputLabel>
                  <Select
                    value={reviewStatusFilter}
                    label="Review Status"
                    MenuProps={reviewFilterSelectMenuProps}
                    onChange={(e) => {
                      setReviewStatusFilter(
                        e.target.value as ReviewStatusFilter,
                      );
                      setCurrentPage(1);
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="pending">Pending review</MenuItem>
                    <MenuItem value="pending-mentions">Pending mentions</MenuItem>
                    <MenuItem value="ready">Ready</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: "0 0 auto", minWidth: 160 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Transcribed</InputLabel>
                  <Select
                    value={transcribedFilter}
                    label="Transcribed"
                    MenuProps={reviewFilterSelectMenuProps}
                    onChange={(e) =>
                      setTranscribedFilter(e.target.value as "all" | "yes" | "no")
                    }
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="yes">Transcribed</MenuItem>
                    <MenuItem value="no">Not transcribed</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: "0 0 auto", minWidth: 160 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Analysis</InputLabel>
                  <Select
                    value={analysisFilter}
                    label="Analysis"
                    MenuProps={reviewFilterSelectMenuProps}
                    onChange={(e) =>
                      setAnalysisFilter(
                        e.target.value as "all" | "analyzed" | "not-analyzed",
                      )
                    }
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="analyzed">Analyzed</MenuItem>
                    <MenuItem value="not-analyzed">Not analyzed</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Stack>
          </Box>

          <ReviewBrandFilterBar
            brandTab={brandTab}
            onBrandTabChange={handleBrandTabChange}
            brands={aggregatedBrands}
            selectedBrandName={selectedBrandName}
            onBrandSelect={handleBrandSelect}
            onBrandEdit={handleBrandEdit}
            getClassificationColor={getClassificationColor}
            isLoading={mentionsLoading}
            validBrandCount={validBrandCount}
            invalidBrandCount={invalidBrandCount}
            brandSearchQuery={brandSearchQuery}
            onBrandSearchChange={setBrandSearchQuery}
          />

          {selectedContentIds.size > 0 && (
            <Paper
              elevation={3}
              sx={{
                position: "sticky",
                top: 8,
                zIndex: (theme) => theme.zIndex.appBar - 1,
                px: 2,
                py: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="body2" fontWeight="bold" sx={{ mr: 1 }}>
                {selectedContentIds.size} selected
              </Typography>
              <Button
                size="small"
                variant="contained"
                onClick={() => setBulkProgramDialogOpen(true)}
                disabled={bulkProgramSaveInProgress}
              >
                Assign program
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => handleBulkAssignProgram(null)}
                disabled={bulkProgramSaveInProgress}
              >
                Remove program
              </Button>
              <Button
                size="small"
                onClick={clearContentSelection}
                disabled={bulkProgramSaveInProgress}
              >
                Clear
              </Button>
              {bulkProgramSaveInProgress && <CircularProgress size={18} />}
            </Paper>
          )}

          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
          >
            <FormControlLabel
              sx={{ mr: "auto" }}
              control={
                <Checkbox
                  size="small"
                  checked={pageAllSelected}
                  indeterminate={pageSomeSelected && !pageAllSelected}
                  disabled={visibleContentIds.length === 0}
                  onChange={(e) => handleSelectPage(e.target.checked)}
                />
              }
              label="Select page"
            />
            <ReviewProgramsPanel podcastId={podcastId} />
            <Button size="small" variant="outlined" onClick={handleExpandAll}>
              Expand all
            </Button>
            <Button size="small" variant="outlined" onClick={handleCollapseAll}>
              Collapse all
            </Button>
          </Box>

          <Stack
            spacing={2}
            sx={{
              opacity: contentRefetching ? 0.5 : 1,
              transition: "opacity 0.15s",
              pointerEvents: contentRefetching ? "none" : undefined,
            }}
          >
            {visibleContent.map((contentItem: ContentItem) => {
              const contentIdStr = contentIdForApi(contentItem._id);
              return (
                <ReviewEpisodeSection
                  key={contentIdStr}
                  contentItem={contentItem}
                  isExpanded={expandedContent.has(contentIdStr)}
                  onToggle={() => toggleContentExpansion(contentIdStr)}
                  brandMentions={episodeMentionsMap.get(contentIdStr) ?? []}
                  mentionsLoading={getMentionsLoadingForContent(contentIdStr)}
                  selectedBrandName={selectedBrandName}
                  formatTime={formatTime}
                  getClassificationColor={getClassificationColor}
                  createYouTubeUrlWithTimestamp={createYouTubeUrlWithTimestamp}
                  formatChileanDate={formatChileanDate}
                  getSourceIcon={getSourceIcon}
                  onCopyContentId={handleCopyContentId}
                  onDeleteBrandMention={handleDeleteBrandMention}
                  onDeleteAllMentions={handleDeleteAllMentions}
                  onCopyMentionId={handleCopyMentionId}
                  onReviewEpisodeMentions={handleReviewEpisodeMentions}
                  onEditMention={handleEditMention}
                  onDuplicateMention={handleDuplicateMention}
                  onMentionDeleted={handleMentionDeleted}
                  onMentionFeedback={handleMentionFeedback}
                  onMentionClassificationChange={handleMentionClassificationChange}
                  onEditProgram={handleEditProgram}
                  onMentionsChanged={handleMentionsChanged}
                  onReviewReadyChange={handleReviewReadyChange}
                  forcedMentionUpdates={dialogSyncedMentions}
                  selected={selectedContentIds.has(contentIdStr)}
                  onSelectChange={handleContentSelectChange}
                />
              );
            })}
          </Stack>

          {visibleContent.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 3, textAlign: "center" }}
            >
              {selectedBrandName
                ? `No episodes with "${selectedBrandName}" on this page.`
                : "No content items found."}
            </Typography>
          )}

          {pagination && pagination.pages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={pagination.pages}
                page={currentPage}
                onChange={(event, page) => setCurrentPage(page)}
                color="primary"
              />
            </Box>
          )}
        </Stack>
      </Box>

      <BulkProgramAssignDialog
        open={bulkProgramDialogOpen}
        onClose={() => setBulkProgramDialogOpen(false)}
        selectedCount={selectedContentIds.size}
        programs={programsData?.programs || []}
        showDigitalOption={showDigitalProgramOption}
        onAssign={handleBulkAssignProgram}
        onCreateAndAssign={handleBulkCreateAndAssignProgram}
        saveInProgress={bulkProgramSaveInProgress}
      />

      <BrandManagementDialog
        open={aliasDialogOpen}
        onClose={() => {
          setAliasDialogOpen(false);
          setSelectedBrand(null);
        }}
        brandName={selectedBrand?.name || ""}
        brandId={selectedBrand?.id}
        currentAliases={selectedBrand?.aliases || []}
        mentionCount={selectedBrand?.mentionCount}
        onAliasesUpdate={handleAliasesUpdate}
        onAntiAliasesUpdate={handleAntiAliasesUpdate}
        onBrandChange={handleBulkSaveBrand}
      />

      <EpisodeMentionReviewDialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        session={mentionReviewSession}
        onSave={handleSaveMentionTimes}
        onReadyChange={handleMentionReadyInDialog}
        onEpisodeReviewReadyChange={async (contentId, ready) => {
          const result = await handleReviewReadyChange(contentId, ready);
          setMentionReviewSession((prev) =>
            prev && contentIdForApi(prev.contentId) === contentIdForApi(contentId)
              ? {
                  ...prev,
                  reviewReadyAt: result?.reviewReadyAt ?? undefined,
                }
              : prev,
          );
          if (ready) {
            advanceToNextReviewEpisodeAfterReady(contentId);
          }
          return result;
        }}
        onMentionsChange={(mentions) =>
          setMentionReviewSession((prev) =>
            prev ? { ...prev, mentions } : null,
          )
        }
        saveInProgress={saveInProgress}
        onRejectMention={handleRejectMentionInDialog}
        onRestoreMention={handleRestoreMentionInDialog}
        onAddMissedMention={handleAddMissedMentionInDialog}
        onDeleteMention={handleDeleteMentionInDialog}
        onCopyMentionId={handleCopyMentionId}
        onAliasesSaved={handleAliasesSavedInDialog}
        onAutoSaved={handleMentionAutoSaved}
        onEditProgram={handleEditProgram}
        episodeNav={reviewEpisodeNav}
      />


      {/* Program Edit Dialog */}
      <ProgramEditDialog
        open={programEditDialogOpen}
        onClose={() => setProgramEditDialogOpen(false)}
        contentId={programEditContentId}
        contentTitle={programEditContentTitle}
        currentProgramId={programEditCurrentProgramId}
        programs={programsData?.programs || []}
        onSave={handleSaveProgram}
        onCreateAndSave={handleCreateAndSaveProgram}
        saveInProgress={programEditSaveInProgress}
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PodcastContentAnalysis;
